import {
  LayerProps,
  DataDrivenPropertyValueSpecification,
} from 'shared/map-exports';
import { FONT_NAMES } from 'features/mapping/styles/constants';
import { SELECTED_FEATURE_HIGHLIGHT_COLOR } from 'features/mapping/mapping.constants';
import {
  CameraArrow,
  CameraArrowOffline,
  CameraArrowSelected,
  CameraPTZBase,
  CameraPTZBaseOffline,
  CameraPTZBaseSelected,
  CameraStationaryBase,
  CameraStationaryBaseOffline,
  CameraStationaryBaseSelected,
  LabelTooltipBackground,
} from 'features/mapping/icons';
import {
  MAX_ZOOM_VISIBILITY_PUBLIC_USER,
  MIN_ZOOM_VISIBILITY_PTZ,
  MIN_ZOOM_VISIBILITY_RECENTLY_MOVED,
  MIN_ZOOM_VISIBILITY_STATIONARY,
  VIEWSHED_FILL_OPACITY,
  VIEWSHED_FILL_OPACITY_SELECTED,
} from './AlertCamerasLayer.constants';
import { scaleIconZoomExpression } from './AlertCamerasLayer.utils';

export const getIconImageLayout = (
  activeCameraId: string = '',
): DataDrivenPropertyValueSpecification<string> => {
  return [
    'match',
    ['get', 'id'],
    ['literal', activeCameraId],
    ['get', 'iconFocused'],
    ['get', 'icon'],
  ];
};

const BASE_ICONS = {
  stationary: {
    default: CameraStationaryBase.name,
    offline: CameraStationaryBaseOffline.name,
    selected: CameraStationaryBaseSelected.name,
  },
  ptz: {
    default: CameraPTZBase.name,
    offline: CameraPTZBaseOffline.name,
    selected: CameraPTZBaseSelected.name,
  },
};

// `selectedIds` are cluster feature ids.
export const getBaseCameraIconExpression = (input: {
  family: 'stationary' | 'ptz';
  selectedIds: string[];
}): DataDrivenPropertyValueSpecification<string> => {
  const icons = BASE_ICONS[input.family];
  return [
    'case',
    ['in', ['to-string', ['id']], ['literal', input.selectedIds]],
    icons.selected,
    ['==', ['get', 'is_offline'], true],
    icons.offline,
    icons.default,
  ];
};

// `selectedIds` are composite ids.
export const getArrowIconExpression = (
  selectedIds: string[],
): DataDrivenPropertyValueSpecification<string> => [
  'case',
  ['in', ['get', 'composite_id'], ['literal', selectedIds]],
  CameraArrowSelected.name,
  ['==', ['get', 'is_offline'], true],
  CameraArrowOffline.name,
  CameraArrow.name,
];

// All users get the same zoom levels for the icons, but non-internal users
// will have the icons hide at zoom levels above `MAX_ZOOM_VISIBILITY_PUBLIC_USER`.
const iconZoomExpression: DataDrivenPropertyValueSpecification<number> = [
  'interpolate',
  ['linear'],
  ['zoom'],
  7,
  0.3,
  11,
  0.5,
  13,
  0.6,
  15,
  0.75,
];

// Stationary / traffic cams get even bigger as the user zooms in, since they start
// off rather small.
const stationaryIconZoomExpression: DataDrivenPropertyValueSpecification<number> =
  [...iconZoomExpression, 17, 1.0];

const getLayerMinZoom = (cameraType: 'stationary' | 'ptz'): number => {
  return cameraType === 'ptz'
    ? MIN_ZOOM_VISIBILITY_PTZ
    : MIN_ZOOM_VISIBILITY_STATIONARY;
};

const getLayerMaxZoomProperty = (input: {
  cameraType: 'stationary' | 'ptz';
  isInternalUser: boolean;
  maxZoom?: number;
}): { maxzoom?: number } => {
  const { cameraType, isInternalUser, maxZoom } = input;
  if (maxZoom) {
    return { maxzoom: maxZoom };
  }
  if (isInternalUser || cameraType === 'stationary') {
    return {};
  }
  return { maxzoom: MAX_ZOOM_VISIBILITY_PUBLIC_USER };
};

export const getBaseIconStyles = (input: {
  cameraType: 'stationary' | 'ptz';
  isInternalUser: boolean;
  activeCameraId?: string;
  minZoom?: number;
  maxZoom?: number;
}): LayerProps => {
  const { cameraType, isInternalUser, activeCameraId, minZoom, maxZoom } =
    input;
  return {
    type: 'symbol',
    minzoom: minZoom || getLayerMinZoom(cameraType),
    ...getLayerMaxZoomProperty({ cameraType, isInternalUser, maxZoom }),
    layout: {
      'icon-image': getIconImageLayout(activeCameraId),
      'icon-allow-overlap': true,
      'icon-ignore-placement': false,
      'icon-size':
        cameraType === 'ptz'
          ? iconZoomExpression
          : stationaryIconZoomExpression,
      'icon-pitch-alignment': 'map',
    },
  };
};

export const labelStyles: LayerProps = {
  type: 'symbol',
  minzoom: MIN_ZOOM_VISIBILITY_PTZ,
  layout: {
    'text-font': FONT_NAMES.regular,
    'text-field': ['get', 'childCameraNames'],
    'text-anchor': 'top',
    'text-justify': 'auto',
    'text-size': 12,
    'text-allow-overlap': ['step', ['zoom'], false, 11, true],
    'text-offset': [0, 4],
    // Icon properties for the background box
    'icon-image': LabelTooltipBackground.name,
    'icon-text-fit': 'both',
    'icon-allow-overlap': ['step', ['zoom'], false, 11, true],
    'text-max-width': 15,
    'icon-text-fit-padding': [8, 12, 8, 12], // [top, right, bottom, left]
  },
  paint: {
    'icon-opacity': 1,
    'icon-color': '#FFFFFF',
  },
  filter: ['>=', ['get', 'childCameraNamesLength'], 1],
};

export const getArrowIconStyles = (input: {
  cameraType: 'stationary' | 'ptz';
  isInternalUser: boolean;
  activeCameraId?: string;
  minZoom?: number;
  maxZoom?: number;
}): LayerProps => {
  const { cameraType, isInternalUser, activeCameraId, minZoom, maxZoom } =
    input;
  return {
    type: 'symbol',
    minzoom: minZoom || getLayerMinZoom(cameraType),
    ...getLayerMaxZoomProperty({ cameraType, isInternalUser, maxZoom }),
    layout: {
      'icon-image': getIconImageLayout(activeCameraId),
      'icon-allow-overlap': true,
      'icon-anchor': 'bottom',
      'icon-ignore-placement': false,
      'icon-size':
        cameraType === 'ptz'
          ? iconZoomExpression
          : scaleIconZoomExpression(stationaryIconZoomExpression, 0.7),
      'icon-offset': [
        'interpolate',
        ['linear'],
        ['zoom'],
        7,
        ['literal', [0, -16]],
        13,
        ['literal', [0, -24]],
      ],
      'icon-rotate': ['get', 'azDeg'],
      'icon-rotation-alignment': 'map',
    },
  };
};

export const getPaintOpacityForActiveId = (
  activeCameraId?: string,
): DataDrivenPropertyValueSpecification<number> | number =>
  activeCameraId
    ? [
        'case',
        ['==', ['get', 'id'], ['literal', activeCameraId]],
        VIEWSHED_FILL_OPACITY_SELECTED,
        VIEWSHED_FILL_OPACITY,
      ]
    : 0;

export const getViewshedFillStyles = (input: {
  cameraType: 'ptz' | 'stationary';
  isInternalUser: boolean;
  activeCameraId?: string;
  minZoom?: number;
  maxZoom?: number;
}): LayerProps => {
  const { cameraType, isInternalUser, activeCameraId, minZoom, maxZoom } =
    input;
  return {
    type: 'fill',
    layout: {},
    minzoom: minZoom || getLayerMinZoom(cameraType),
    ...getLayerMaxZoomProperty({ cameraType, isInternalUser, maxZoom }),
    paint: {
      'fill-color': '#3388FF',
      'fill-opacity': getPaintOpacityForActiveId(activeCameraId),
    },
  };
};

export const getViewshedStrokeStyles = (input: {
  cameraType: 'ptz' | 'stationary';
  isInternalUser: boolean;
  activeCameraId?: string;
  minZoom?: number;
  maxZoom?: number;
}): LayerProps => {
  const { cameraType, isInternalUser, activeCameraId, minZoom, maxZoom } =
    input;
  return {
    type: 'line',
    layout: {},
    ...getLayerMaxZoomProperty({ cameraType, isInternalUser, maxZoom }),
    minzoom: minZoom || getLayerMinZoom(cameraType),
    paint: {
      'line-color': '#3388FF',
      'line-width': ['interpolate', ['linear'], ['zoom'], 8, 2, 18, 6],
      'line-opacity': getPaintOpacityForActiveId(activeCameraId),
    },
  };
};

export const getAzimuthLineStyles = (isInternalUser: boolean): LayerProps => ({
  type: 'line',
  minzoom: MIN_ZOOM_VISIBILITY_RECENTLY_MOVED,
  paint: {
    'line-color': ['get', 'color'],
    'line-width': [
      'interpolate',
      ['linear'],
      ['zoom'],
      MIN_ZOOM_VISIBILITY_RECENTLY_MOVED,
      2,
      18,
      6,
    ],
    'line-opacity': isInternalUser ? 1 : 0,
  },
});

export const getAzimuthLineMouseOverStyles = (
  isVisible: boolean,
): LayerProps => ({
  type: 'line',
  minzoom: MIN_ZOOM_VISIBILITY_PTZ,
  paint: {
    'line-color': SELECTED_FEATURE_HIGHLIGHT_COLOR,
    'line-width': ['interpolate', ['linear'], ['zoom'], 8, 2, 18, 6],
    'line-opacity': isVisible ? 1 : 0,
  },
});
